import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../../../lib/supabase';

interface AddLotModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialData?: {
    lot_id: string;
    lot_perm_use: string | null;
    lot_zone: string | null;
    lot_serviced: string;
    lot_community: string | null;
    lot_avail: string;
    created_date: string;
  } | null;
}

export default function AddLotModal({ isOpen, onClose, onSuccess, initialData }: AddLotModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    lotId: '',
    community: '',
    permittedUse: '',
    zoneType: '',
    isServiced: 'N',
    isAvailable: 'Y'
  });

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({
        lotId: initialData.lot_id,
        community: initialData.lot_community || '',
        permittedUse: initialData.lot_perm_use || '',
        zoneType: initialData.lot_zone || '',
        isServiced: initialData.lot_serviced,
        isAvailable: initialData.lot_avail
      });
    } else {
      // Reset form when adding new lot
      setFormData({
        lotId: '',
        community: '',
        permittedUse: '',
        zoneType: '',
        isServiced: 'N',
        isAvailable: 'Y'
      });
    }
  }, [initialData]);

  const { data: communities, isLoading: isLoadingCommunities } = useQuery({
    queryKey: ['communities'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('communities')
        .select('comm_id, comm_name')
        .order('comm_name');

      if (error) throw error;
      return data || [];
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      if (initialData) {
        // Update existing lot
        const { error: updateError } = await supabase
          .from('land_lots')
          .update({
            lot_community: formData.community,
            lot_perm_use: formData.permittedUse || null,
            lot_zone: formData.zoneType || null,
            lot_serviced: formData.isServiced,
            lot_avail: formData.isAvailable
          })
          .eq('lot_id', initialData.lot_id);

        if (updateError) throw updateError;
      } else {
        // Add new lot
        const { error: insertError } = await supabase
          .from('land_lots')
          .insert([{
            lot_id: formData.lotId,
            lot_community: formData.community,
            lot_perm_use: formData.permittedUse || null,
            lot_zone: formData.zoneType || null,
            lot_serviced: formData.isServiced,
            lot_avail: formData.isAvailable
          }]);

        if (insertError) throw insertError;
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error creating lot:', err);
      setError(err instanceof Error ? err.message : 'Failed to create lot');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-lg font-medium">{initialData ? 'Edit Lot' : 'Add New Lot'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Lot ID *
            </label>
            <input
              type="text"
              value={formData.lotId}
              onChange={(e) => setFormData(prev => ({ ...prev, lotId: e.target.value }))}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
              required
              disabled={!!initialData} // Disable Lot ID field when editing
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Community *
            </label>
            <select
              value={formData.community}
              onChange={(e) => setFormData(prev => ({ ...prev, community: e.target.value }))}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
              required
            >
              <option value="">Select a community</option>
              {communities?.map((community) => (
                <option key={community.comm_id} value={community.comm_id}>
                  {community.comm_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Permitted Use
            </label>
            <select
              value={formData.permittedUse}
              onChange={(e) => setFormData(prev => ({ ...prev, permittedUse: e.target.value }))}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
            >
              <option value="">Select permitted use</option>
              <option value="Residential">Residential</option>
              <option value="Commercial">Commercial</option>
              <option value="Mixed Use">Mixed Use</option>
              <option value="Industrial">Industrial</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Zone Type
            </label>
            <select
              value={formData.zoneType}
              onChange={(e) => setFormData(prev => ({ ...prev, zoneType: e.target.value }))}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
            >
              <option value="">Select zone type</option>
              <option value="R1">R1 - Residential</option>
              <option value="C1">C1 - Commercial</option>
              <option value="I1">I1 - Industrial</option>
              <option value="M1">M1 - Mixed Use</option>
            </select>
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isServiced === 'Y'}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  isServiced: e.target.checked ? 'Y' : 'N' 
                }))}
                className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
              />
              <span className="ml-2 text-sm text-gray-700">Serviced</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isAvailable === 'Y'}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  isAvailable: e.target.checked ? 'Y' : 'N' 
                }))}
                className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
              />
              <span className="ml-2 text-sm text-gray-700">Available</span>
            </label>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary-dark disabled:opacity-50"
            >
              {isSubmitting ? (initialData ? 'Saving...' : 'Creating...') : (initialData ? 'Save Changes' : 'Create Lot')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
